import type { Medication } from '../types';

export type NotificationSetting = 'all' | 'logs' | 'meds' | 'none';

export interface ReminderPreferences {
  notificationSetting: NotificationSetting;
  logReminderAM?: string;
  permission?: NotificationPermission | 'unsupported';
  use24h?: boolean;
}

const DEFAULT_PREFERENCES: ReminderPreferences = {
  notificationSetting: 'all',
};

function readSettings(): Record<string, unknown> {
  try {
    return JSON.parse(localStorage.getItem('koda_settings') || '{}') as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function loadReminderPreferences(): ReminderPreferences {
  const settings = readSettings();
  const notificationSetting = settings.notificationSetting;
  return {
    ...DEFAULT_PREFERENCES,
    notificationSetting:
      notificationSetting === 'all' ||
      notificationSetting === 'logs' ||
      notificationSetting === 'meds' ||
      notificationSetting === 'none'
        ? notificationSetting
        : DEFAULT_PREFERENCES.notificationSetting,
    logReminderAM: typeof settings.logReminderAM === 'string' ? settings.logReminderAM : undefined,
    permission:
      settings.permission === 'granted' ||
      settings.permission === 'denied' ||
      settings.permission === 'default' ||
      settings.permission === 'unsupported'
        ? settings.permission
        : undefined,
    use24h: Boolean(settings.use24h),
  };
}

export function isWithinTreatmentWindow(medication: Medication, now = new Date()): boolean {
  const today = now.toISOString().slice(0, 10);
  if (medication.treatment_start && today < medication.treatment_start) return false;
  if (medication.treatment_end && today > medication.treatment_end) return false;
  return true;
}

function parseTimeValue(timeValue: string): { hours: number; minutes: number } | null {
  const match = timeValue.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

export function isWithinTimeWindow(timeValue: string, now = new Date(), windowMinutes = 5): boolean {
  const parsed = parseTimeValue(timeValue);
  if (!parsed) return false;

  const scheduled = new Date(now);
  scheduled.setHours(parsed.hours, parsed.minutes, 0, 0);

  const deltaMinutes = Math.abs(now.getTime() - scheduled.getTime()) / 60000;
  return deltaMinutes <= windowMinutes;
}

export function shouldFireMedReminder(
  medication: Medication,
  preferences: ReminderPreferences,
  now = new Date(),
): boolean {
  if (preferences.notificationSetting === 'none' || preferences.notificationSetting === 'logs') return false;
  if (medication.reminders_enabled === false) return false;
  if (!isWithinTreatmentWindow(medication, now)) return false;
  return medication.timeslots.some(timeValue => isWithinTimeWindow(timeValue, now));
}

export function shouldFireLogReminder(preferences: ReminderPreferences, now = new Date()): boolean {
  if (preferences.notificationSetting === 'none' || preferences.notificationSetting === 'meds') return false;
  if (!preferences.logReminderAM) return false;
  return isWithinTimeWindow(preferences.logReminderAM, now);
}
